import { type Area } from 'react-easy-crop';

const CROPPED_AVATAR_SIZE_PX = 400;
const FALLBACK_OUTPUT_MIME_TYPE = 'image/jpeg';
const PRIMARY_OUTPUT_MIME_TYPE = 'image/webp';
const OUTPUT_QUALITY = 0.92;

function loadImage(sourceUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load source photo'));
    image.src = sourceUrl;
  });
}

function exportCanvasBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
): Promise<Blob | null> {
  return new Promise(resolve => {
    canvas.toBlob(blob => resolve(blob), mimeType, OUTPUT_QUALITY);
  });
}

export async function createCroppedAvatarBlob(
  sourceUrl: string,
  croppedAreaPixels: Area,
): Promise<Blob> {
  const image = await loadImage(sourceUrl);
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Canvas 2D context is unavailable');
  }

  canvas.width = CROPPED_AVATAR_SIZE_PX;
  canvas.height = CROPPED_AVATAR_SIZE_PX;

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(
    image,
    Math.round(croppedAreaPixels.x),
    Math.round(croppedAreaPixels.y),
    Math.round(croppedAreaPixels.width),
    Math.round(croppedAreaPixels.height),
    0,
    0,
    CROPPED_AVATAR_SIZE_PX,
    CROPPED_AVATAR_SIZE_PX,
  );

  const webpBlob = await exportCanvasBlob(canvas, PRIMARY_OUTPUT_MIME_TYPE);

  if (webpBlob) {
    return webpBlob;
  }

  const jpegBlob = await exportCanvasBlob(canvas, FALLBACK_OUTPUT_MIME_TYPE);

  if (jpegBlob) {
    return jpegBlob;
  }

  throw new Error('Failed to export cropped avatar');
}
